import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookingApi } from 'shared/api';
import { MonthNames, WeekDaysArray } from 'shared/consts/calendar.consts';
import { LeftArrowSvg, RightArrowSvg } from 'shared/svg';
import { MonthCalendarDayType } from 'shared/api/booking/interfaces/month-calendar-day-type.enum';
import { IMonthCalendarDay } from 'shared/api/booking/interfaces/calendar-card-month.interface';
import {
  ICalendarDayBookings,
  ICalendarDayBreaks,
} from 'shared/api/booking/interfaces/calendar-day.type';
import { EmployeeWorkDayPopup } from 'shared/ui/employee-work-day-popup/employee-work-day-popup';
import styles from './mini-schedule-calendar.module.scss';

interface IMiniScheduleCalendarProps {
  pointId?: string;
  employeeId?: string;
  employeeName?: string;
  employeeImgUrl?: string;
  currentDate?: string;
  refreshKey?: number;
}

const DAY_TYPE_CLASS: Record<string, string> = {
  [MonthCalendarDayType.WORK_DAY]: '',
  [MonthCalendarDayType.DAY_OFF]: 'day_off',
  [MonthCalendarDayType.VACATION]: 'day_vacation',
  [MonthCalendarDayType.SICK_LEAVE]: 'day_sick',
  [MonthCalendarDayType.ADMINISTRATIVE]: 'day_admin',
  [MonthCalendarDayType.NOT_IN_CURRENT_MONTH]: 'day_other_month',
  [MonthCalendarDayType.OLD_DAY]: 'day_old',
};

export const MiniScheduleCalendar: React.FC<IMiniScheduleCalendarProps> = ({
  pointId,
  employeeId,
  employeeName = '',
  employeeImgUrl = '',
  refreshKey,
}) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [days, setDays] = useState<IMonthCalendarDay[]>([]);
  const [selectedDay, setSelectedDay] = useState<IMonthCalendarDay | null>(
    null
  );

  const fetchSchedule = async () => {
    if (!pointId || !employeeId) return;
    const firstDayOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    try {
      const data = await BookingApi.getCalendarMonthInfo({
        month: month + 1,
        year,
        pointId,
        employeeId,
        currentDate: firstDayOfMonth,
      });
      if (data?.result) {
        setDays(data.result);
      }
    } catch {
      setDays([]);
    }
  };

  useEffect(() => {
    fetchSchedule();
  }, [month, year, pointId, employeeId, refreshKey]);

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const handleDayClick = (day: IMonthCalendarDay) => {
    if (
      day.type === MonthCalendarDayType.OLD_DAY ||
      day.type === MonthCalendarDayType.NOT_IN_CURRENT_MONTH
    ) {
      return;
    }
    setSelectedDay(day);
  };

  const handleClosePopup = () => setSelectedDay(null);

  return (
    <div className={styles.calendar}>
      <div className={styles.calendar__header}>
        <button className={styles.calendar__arrow} onClick={prevMonth}>
          <LeftArrowSvg />
        </button>
        <span className={styles.calendar__month}>
          {MonthNames[month]} {year}
        </span>
        <button className={styles.calendar__arrow} onClick={nextMonth}>
          <RightArrowSvg />
        </button>
      </div>

      <div className={styles.calendar__weekdays}>
        {WeekDaysArray.map((d) => (
          <div key={d} className={styles.calendar__weekday}>
            {d}
          </div>
        ))}
      </div>

      <div className={styles.calendar__grid}>
        {days.map((day, index) => {
          const date = new Date(day.currDate).getDate();
          const typeClass = DAY_TYPE_CLASS[day.type] || '';
          return (
            <div
              key={index}
              className={`${styles.calendar__day} ${styles[typeClass] || ''}`}
              onClick={() => handleDayClick(day)}
            >
              {date}
            </div>
          );
        })}
      </div>

      <div className={styles.calendar__legend}>
        <span className={styles.legend__item}>
          <span className={styles.dot_work} /> Рабочий
        </span>
        <span className={styles.legend__item}>
          <span className={styles.dot_off} /> Выходной
        </span>
        <span className={styles.legend__item}>
          <span className={styles.dot_vacation} /> Отпуск
        </span>
        <span className={styles.legend__item}>
          <span className={styles.dot_sick} /> Больничный
        </span>
      </div>

      {selectedDay &&
        employeeId &&
        createPortal(
          <EmployeeWorkDayPopup
            employee={{
              employee: {
                id: employeeId,
                fio: employeeName,
                imgUrl: employeeImgUrl,
              },
              workDay: {
                startedAt: null,
                finishedAt: null,
                isDayOff: selectedDay.type === MonthCalendarDayType.DAY_OFF,
              },
              breaks: [] as unknown as [ICalendarDayBreaks],
              bookings: [] as unknown as [ICalendarDayBookings],
            }}
            date={new Date(selectedDay.currDate)}
            onClose={handleClosePopup}
            onUpdate={fetchSchedule}
          />,
          document.body
        )}
    </div>
  );
};
